import React from "react";
import MalattiaRaraTemplate from "../../components/malattia-rara/MalattiaRaraTemplate";
import image1 from "../../assets/malattia_fabry.jpg";

const MalattiaFabryPage: React.FC = () => (
  <MalattiaRaraTemplate
    titolo="Malattia di Fabry"
    descrizione={[
      "La malattia di Anderson-Fabry, o malattia di Fabry (MF), è una rara patologia genetica legata al cromosoma X e appartiene al gruppo delle malattie da accumulo lisosomiale. È causata da mutazioni del gene GLA, che determinano una carenza parziale o totale dell'enzima lisosomiale α-galattosidasi A (α-GAL A), responsabile della degradazione di alcuni lipidi, in particolare dello sfingolipide globotriaosilceramide (Gb3) e della sua forma deacetilata globotriaosilsfingosina (Lyso-Gb3). La MF ha un decorso progressivo che coinvolge contemporaneamente diversi organi e apparati. Le manifestazioni cliniche variano per tempistica (dall'infanzia all'età adulta) e gravità, in base a fattori genetici e ambientali. Le mutazioni missenso, associate a un’attività residua di α-GAL A, generalmente determinano forme più lievi, talvolta limitate a uno o pochi organi, come nel caso delle varianti cardiaca e renale.",
      "A livello cardiaco, la malattia provoca l’accumulo di sfingolipidi in tutti i tessuti del cuore. Questo può portare a ipertrofia e disfunzione miocardica nei cardiomiociti, a alterazioni valvolari con conseguente insufficienza mitralica e aortica, e a rimodellamento elettrofisiologico, aumentando il rischio di aritmie. Inoltre, il coinvolgimento dell’endotelio può innescare processi infiammatori e fibrotici, favorendo disfunzione endoteliale e ischemia microvascolare coronarica.",
      "La gestione della MF prevede modifiche dello stile di vita, controllo dei fattori di rischio cardiovascolare e terapia mirate alla gestione dello scompenso cardiaco. Prima del 2001, le terapie disponibili si limitavano a quelle per le comuni patologie cardiovascolari, senza trattamenti specifici per la MF. La svolta è arrivata con l’introduzione della terapia enzimatica sostitutiva (ERT), grazie agli enzimi agalsidasi-alfa e agalsidasi-beta, somministrati per via endovenosa ogni due settimane.",
      "Questo ha permesso di modificare in modo significativo la storia naturale della malattia, migliorando la prognosi dei pazienti.",
    ]}
    immagineDescrizione={image1}
    altImmagineDescrizione="Accumuli di Gb3 nei tessuti cardiaci nella malattia di Fabry"
    sintomi={[
      "Manifestazioni cardiache: cardiomiopatia ipertrofica, aritmie, dolore toracico, infarto miocardico, disturbi della conduzione",
      "Manifestazioni renali: proteinuria, insufficienza renale progressiva",
      "Manifestazioni neurologiche: acroparestesie (dolore urente agli arti), ictus ischemico, attacchi ischemici transitori (TIA)",
      "Lesioni cutanee: angiocheratomi (piccole papule rosso-violacee)",
      "Ipoidrosi o anidrosi (ridotta o assente sudorazione)",
      "Disturbi gastrointestinali: dolore addominale, diarrea, nausea",
      "Manifestazioni oculari: cornea verticillata, cataratta",
      "Acufeni, ipoacusia",
      "Febbre ricorrente",
      "Affaticamento cronico",
    ]}
    diagnosi={[
      "Dosaggio dell'attività enzimatica dell'alfa-galattosidasi A nel plasma, nei leucociti o in spot di sangue secco (ridotta nei maschi, può essere normale nelle femmine)",
      "Test genetico per identificare mutazioni nel gene GLA (gold standard diagnostico, particolarmente importante nelle femmine)",
      "Quantificazione dei biomarcatori: Gb3 e lyso-Gb3 plasmatici e urinari",
      "Ecocardiogramma: ipertrofia ventricolare sinistra (in particolare del setto basale), anomalie valvolari",
      "Risonanza magnetica cardiaca (RMC): ipertrofia, fibrosi miocardica con pattern di enhancement tardivo nella regione inferolaterale basale",
      "Elettrocardiogramma (ECG): ipertrofia ventricolare sinistra, anomalie della ripolarizzazione, intervallo PR corto, disturbi della conduzione",
      "Valutazione della funzionalità renale: GFR, proteinuria",
      "Esame neurologico e RMN cerebrale",
      "Esame dermatologico e oftalmologico",
    ]}
    trattamento={[
      "Terapia enzimatica sostitutiva (ERT) con agalsidasi alfa o beta: infusione endovenosa ogni 2 settimane",
      "Terapia chaperonica con migalastat (nelle forme con mutazioni suscettibili): stabilizza l'enzima endogeno",
      "Terapia convenzionale per le complicanze cardiache: beta-bloccanti, ACE-inibitori/sartani, diuretici",
      "Gestione delle aritmie: antiaritmici, anticoagulanti, pacemaker o defibrillatore impiantabile se indicato",
      "Trattamento dell'insufficienza renale: dialisi, trapianto renale nei casi avanzati",
      "Terapia del dolore neuropatico: carbamazepina, gabapentin, pregabalin",
      "Prevenzione degli eventi cerebrovascolari: antiaggreganti, controllo dei fattori di rischio cardiovascolare",
      "Modifiche dello stile di vita: esercizio fisico moderato, dieta equilibrata, evitare temperature estreme",
      "Supporto psicologico",
      "Consulenza genetica e screening familiare",
    ]}
    immagineTrattamento="https://via.placeholder.com/400x300"
    altImmagineTrattamento="Terapia enzimatica sostitutiva per la malattia di Fabry"
    epidemiologia={{
      prevalenza:
        "1 caso ogni 40.000-60.000 persone (forme classiche), fino a 1:3.000 considerando varianti cardiache",
      incidenza: "1 caso ogni 80.000 nati vivi",
      etaMedia:
        "Diagnosi spesso tardiva (30-40 anni), sintomi da infanzia/adolescenza nelle forme classiche",
      rapportoSessi:
        "Colpisce entrambi i sessi, manifestazioni più gravi nei maschi",
    }}
    riferimenti={[
      {
        nome: "Ortiz A, et al. Fabry disease revisited: Management and treatment recommendations for adult patients. Mol Genet Metab. 2018",
        link: "https://www.esempio.it/riferimento1",
      },
      {
        nome: "Linhart A, et al. Cardiac manifestations of Anderson-Fabry disease: results from the international Fabry outcome survey. Eur Heart J. 2007",
        link: "https://www.esempio.it/riferimento2",
      },
      {
        nome: "Fabry International Network - Organizzazione di supporto per pazienti con malattia di Fabry",
        link: "https://www.esempio.it/riferimento3",
      },
      {
        nome: "Orphanet - Informazioni sulla Malattia di Fabry",
        link: "https://www.esempio.it/riferimento4",
      },
    ]}
  />
);

export default MalattiaFabryPage;
